import type { ParsedQs } from 'qs';
import type { Attribution, MarketingData } from './marketing';
import type { ProductTierMatch } from './product-detail';
import type { RequestUser } from './request';
import type { Obligation } from './ztb';

import type { BiztechMarketingContext, Content, NewAccountInfoWithPassword } from '.';
import type { Currency } from './currency';

type OfferDetail = {
  accordionHeaderText: string;
  offerInfo?: Array<string>;
  features?: Array<string>;
  showOfferName?: boolean;
  openAccordion?: boolean; // Set this if the accordion should be open by default.
};

type OfferDetails = Array<OfferDetail>;

export type OfferItem = {
  name?: string;
  price: number;
  productCode?: string;
  subtext?: string;
  // Rendered with a strikethrough price + a "Waived" tag by consumers that support it (e.g. Order
  // Review's additionalItems rendering) instead of contributing to the order total.
  waived?: boolean;
  // i18n key for the "Waived" badge text rendered next to this item when `waived` is true — lets an
  // offer override the label (e.g. localized/product-specific copy) instead of relying on the
  // consumer's hardcoded default key.
  waivedLabelKey?: string;
};

type ThankYouContent = {
  customHeadingText?: string;
  middleTextContent?: string;
  resourceHeading?: string;
  resourceContent?: Array<Content>;
  topTextContent?: Array<string>;
  icon?: string; // skylab brand icons only name of icon
};

// Content for the Order Confirmation page (src/client/components/offer/order-confirmation/) — the
// post-purchase page for offers reached through the Product Catalog / PDP flow, rendered at
// /${offerFlow}/thank-you in place of the classic ThankYouPage for any offer whose `pages` include
// Pages.OrderReview. Deliberately separate from ThankYouContent: this page's layout is fixed
// (Avi image, "{offerName} is confirmed" heading, exactly two lines of supplemental text, a
// standing "Go to Home" button, and an optional product-specific onboarding button) rather than
// the classic page's much more flexible/composable content model.
export type OrderConfirmationContent = {
  supplementalText: Array<string>;
  onboardingUrl?: string;
};

export type PlanIncludes = {
  heading: string;
  items: Array<string>;
};

export type OfferOrderSummary = {
  additionalItems?: Array<OfferItem>;
  avataxRequired?: boolean;
  heading?: string;
  subheading?: string;
  planIncludes?: PlanIncludes;
  // Fixed sales-tax amount for Order Review to use instead of the dynamic AvaTax lookup (see
  // getOrderSalesTax) — for offers where the tax amount is already known rather than calculated
  // per-customer/per-jurisdiction at checkout time.
  salesTaxAmount?: number;
  secondaryHeading?: string;
  secondaryItems?: Array<OfferItem>;
  showBillingTermsInOrderSummary?: boolean;
  showDiscountedSubTotal?: boolean;
  // Opt-in for Order Review to render the matched Product Catalog tier's `finePrint` text (see
  // ProductTierMatch/tier.finePrint) below the total. Defaults to not showing it, since that fine
  // print is authored for the PDP pricing card, not necessarily for every offer's Order Review page.
  showTierFinePrint?: boolean;
  totalSubtext?: Array<string>;
  totalText?: string;
};

export type OfferFlow =
  | null // TODO: look into removing null
  | 'add-integration'
  | 'avalara-licensing'
  | 'avalara-tax-research'
  | 'avatax-sandbox'
  | 'avatax-free-trial'
  | 'dbna'
  | 'ecm-pro'
  | 'elr'
  | 'exemptions'
  | 'extractor'
  | 'free-trial'
  | 'ioss'
  | 'license-guidance'
  | 'license-preparation'
  | 'managed-returns'
  | 'risk-assessment'
  | 'sales-tax-registrations';

type QueryName = string;
export type OfferSpecificQueryParams = {
  [StateName: string]: QueryName;
};

export type AllowedUsers = 'EXISTING' | 'NEW';
export type UsesConnectors = 'OPTIONAL' | 'REQUIRED';

// Volume-based pricing: the unit price depends on how many units the customer buys, rather than
// being a single figure on the plan. Bands must be contiguous and cover the whole purchasable range;
// a quantity outside every band has no price (see getUnitPriceForQuantity)
export type VolumePricingTier = {
  minQuantity: number;
  maxQuantity: number;
  unitPrice: number;
};

export type CommonOfferDetails = {
  adjustableQuantity?: boolean;
  volumePricingTiers?: ReadonlyArray<VolumePricingTier>;
  allowedUsers?: AllowedUsers;
  attributionDefaults: Attribution;
  initializeNewAccounts?: boolean;
  isEmeaOffer?: boolean;
  isFreeTrial?: boolean;
  isPaymentRequired: boolean;
  minQuantity?: number;
  maxQuantity?: number;
  obligationsRequiredForPlans?: boolean;
  offerName: string;
  offerSpecificQueryParams?: OfferSpecificQueryParams;
  taxCode?: string;
  usesConnectors?: UsesConnectors;
};

type TermsAndConditionsLink = {
  text: string;
  url: string;
};

// A single crumb for Order Review's breadcrumb trail (SBreadcrumbs' navItems). `labelKey` is an i18n
// key rather than raw text, matching how the rest of this offer-config surface passes translatable
// strings. Omit `url` for the current-page crumb (no link).
export type OrderReviewBreadcrumbItem = {
  labelKey: string;
  url?: string;
};

type EsbPlanClientDetails = { featuresList: Array<string> };
export type ExemptionsPlanClientDetails = { certificatesIncluded: string };
export type IossPlanClientDetails = { currency: Currency };
type OfferSpecificPlanClientDetails =
  | EsbPlanClientDetails
  | ExemptionsPlanClientDetails
  | IossPlanClientDetails
  | object;

export type OfferPlanClientDetails = OfferSpecificPlanClientDetails & {
  campaignId?: string;
  currency?: Currency;
  isSubscribedToPlan?: boolean;
  name: string;
  orderSummaryOverrides?: Partial<OfferOrderSummary>;
  planCountry?: string;
  planSpecificBillingTerms?: Array<string>;
  // Kyle - if we end up having pricing being calculated for offers sold via API we
  // need to make sure it is consistently done from client to server via productItems
  productItems?: Array<OfferItem>;
  tier?: number;
};

type AddItem = { productCode: string };

export type OfferSpecificSettings = {
  [setting: string]: string | null;
};

export type AddOfferRequest = {
  // TODO (Andrew) - when necessary, we should be able to make connectorId part
  //                 of the validatedOfferSpecificSettings. We should also be
  //                 able to do the same for tierNumber, although that may take
  //                 a bit more finagling, since the properties are only typed
  //                 to allow strings or null values. In fact, we should
  //                 make all the optional params part of the offer specific
  //                 settings, since that will address a FlowFixMe
  accountId?: string;
  connectorId?: string;
  customerAccountId?: string;
  discountPercent?: number;
  isPaymentRequired: boolean;
  marketingContext: {
    opportunity: BiztechMarketingContext;
  };
  offerCode: string;
  paymentMethodId?: string | null;
  quantity?: number;
  salesRepId: string;
  shouldUseUpgradeApi?: boolean;
  tierNumber?: number;
  upgradeSubtype?: string;
  validatedOfferSpecificSettings?: OfferSpecificSettings;
};

export type OfferPlan = OfferPlanClientDetails & {
  addItems?: Array<AddItem>;
  checkEligibility?: (obligations: Array<Obligation>) => boolean;
  emailDetails?: EmailDetails;
  obligationFeatureNameRegex?: RegExp;
  offerCode: string;
  planSpecificOfferParams?: Partial<AddOfferRequest>;
  upgradeSubtype?: string;
};

export enum Pages {
  Plans = 'plans',
  GetStarted = 'get-started',
  Company = 'company',
  Payment = 'payment',
  ReviewOrder = 'review-order',
  OrderReview = 'order-review',
  Summary = 'summary',
  SettingUp = 'setting-up',
  ThankYou = 'thank-you',
  Redirect = 'redirect',
  SelectConnector = 'select-connector',
  AddBillingContact = 'add-billing-contact',
}

export type OfferClientDetails = CommonOfferDetails & {
  allowEmbeddedView?: boolean;
  analyticsProductCategory: string;
  buyButtonTextOverride?: string;
  deliverySystemToPollFor?: string;
  offerDetails?: OfferDetails;
  offerFlow: OfferFlow;
  orderConfirmationContent?: OrderConfirmationContent;
  orderSummary: OfferOrderSummary;
  // Breadcrumb trail shown above the Order Review heading (e.g. "Product catalog > ECM > ECM").
  // Omit to render no breadcrumb at all — most offers using Pages.OrderReview today have no
  // Product Catalog PDP path leading into it.
  orderReviewBreadcrumbs?: Array<OrderReviewBreadcrumbItem>;
  pages: Array<Pages>;
  // The Product Catalog product/tier that sells this offer, if any — resolved server-side in
  // getOfferClientDetailsWithPlans (src/server/routes/helpers/offers.ts) since Order Review has no
  // product-catalog slug of its own to look this up by.
  productTierMatch?: ProductTierMatch;
  redirectUrl?: string;
  reviewOrder?: boolean;
  showEmbeddedSideContent?: boolean;
  // if no termsAndConditions set, general terms and conditions will be used
  termsAndConditions?: Array<TermsAndConditionsLink>;
  thankYouContent: ThankYouContent;
};

export type ClientOfferWithPlans = OfferClientDetails & {
  plans: Array<OfferPlanClientDetails>;
};

export type EmailDetails = {
  renewalDate?: string;
  button?: string;
  additionalText?: string;
  primaryContactEmail?: string;
};

export type CheckIsValidConnectorParams = {
  connectorId: string;
  obligations: Array<Obligation>;
  user?: RequestUser;
};

type EmailTemplateName =
  // TODO: replace licensing email with the below after AAA-7334 is resolved
  // np'sales-tax-registration-purchase-confirmation'
  'avalara-licensing-purchase-confirmation' | 'dynamic-shared-email';

export type OfferServerDetails = CommonOfferDetails & {
  checkIsValidConnector?: (_: CheckIsValidConnectorParams) => Promise<boolean>;
  defaultPlanName?: string;
  emailTemplateName?: EmailTemplateName;
  magicLinkRedirectUrl?: string;
  skipConfirmationEmail?: boolean;
  snsTag?: string;
};

export type ServerOfferWithPlans = OfferServerDetails & {
  shouldUseUpgradeApi: boolean;
  plans: Array<OfferPlan>;
};

export type GetClientDetailsParams = {
  isEmbedded: boolean;
  isLoggedIn: boolean;
  hasSelectedConnector: boolean;
  avataxAccountId?: string;
};

export type GetPlansParams = {
  obligations?: Array<Obligation> | null;
  query?: ParsedQs;
  avataxAccountId?: string;
};

export type OfferDefinition = {
  readonly featureFlag?: string;
  readonly offerFlow: OfferFlow;
  checkUpgradeApiEligibility?: (obligations: Array<Obligation>) => boolean;
  getPlans: (params: GetPlansParams) => Promise<Array<OfferPlan>>;
  // NOTE (Andrew) - if getClientDetails is missing, this indicates an API-only offer with no
  //                 corresponding Buy UI:
  getClientDetails?: (arg0: GetClientDetailsParams) => Promise<OfferClientDetails>;
  getServerDetails: () => OfferServerDetails;
};

export type OfferPurchaseRequest = {
  discountCode?: string | null;
  marketingData?: MarketingData;
  offerFlow: OfferFlow;
  offerSpecificSettings?: OfferSpecificSettings;
  paymentMethodId?: string;
  quantity?: number;
  selectedPlanName?: string;
  totalCost?: string;
  connectorId?: string;
  salesTax?: number | null;
};

export type CreateAccountForOfferRequest = OfferPurchaseRequest & {
  accountInfo: NewAccountInfoWithPassword;
  retryKey?: string;
};

export type DiscountConfig = {
  discountPercentage: number | undefined;
  additionalLegalText: string | null;
  discountName: string | null;
};

export type Offer = {
  offerCode: string;
  createdDate: string;
  correlationId: string;
  connectorId: string;
  dealinfoId: string;
  opportunityId: string;
  quoteId: string;
  orderId: string;
  orderProcessingStatus: 'Pending' | 'Success';
  contractId: string;
  contractStatus: string;
  fasterProvisioningSent: boolean;
};

export type SoldOffers = {
  customerAccountId: string;
  offers: Array<Offer>;
  success: boolean;
  errorMessage?: string;
};

export type SoldOffersResponse = {
  count: number;
  value: Array<SoldOffers>;
};
